import React, {ReactElement, useMemo, ChangeEvent} from 'react';
import {Typography} from '@customink/pigment-react';
import Tooltip from '@mui/material/Tooltip';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import {useAppDispatch, useAppSelector} from '../../hooks';
import {
  addPendingDetail,
  selectBannerFillType,
  removePendingDetail
} from '../../slices/storesSlice';
import ImageUploader from '../ImageUploader';
import ClearExistingImage from './ClearExistingImage';
import InfoIconSm from '../../assets/icons/infosmall.svg';
import {BannerFillType, PendingStoreDetails} from '../../models/storeDetails';
import type {AppDispatch} from '../../store';
import './index.scss';

type BannerFillSettingsType = {
  [key in BannerFillType]: PendingStoreDetails;
};

const bannerFillSettingsMap: BannerFillSettingsType = {
  solidColor: {
    heroLogoVisibility: 'hero_logo'
  },
  image: {
    heroLogoVisibility: 'hero_background'
  },
  none: {
    heroLogoVisibility: 'hero_logo'
  },
  custom: {}
};

const dispatchBannerChanges = (
  settingsKey: BannerFillType,
  appDispatch: AppDispatch
): void => {
  const settings = bannerFillSettingsMap[settingsKey];
  Object.keys(settings).forEach(
    (customizationKey: keyof PendingStoreDetails) => {
      appDispatch(
        addPendingDetail({
          key: customizationKey,
          val: settings[customizationKey]
        })
      );
    }
  );
};

const HeroImageForm: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch();
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const localImages = useAppSelector(state => state.stores.localImages);
  const currentFillType = useAppSelector(selectBannerFillType);

  const onHeaderTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value as BannerFillType;
    dispatchBannerChanges(val, dispatch);
  };

  const onHeaderBlobChange = () => {
    dispatchBannerChanges('image', dispatch);
  };

  const onUploadCancel = () => {
    dispatch(removePendingDetail('heroImage'));
    dispatch(removePendingDetail('heroLogoVisibility'));
  };

  const imagePreview = useMemo(() => {
    const heroImageUrl = localImages.heroImage || storeDetails?.heroImageUrl;

    let title: string | undefined;
    if (localImages.heroImage) title = 'Image Preview:';
    else if (storeDetails?.heroImageUrl) title = 'Current Image:';

    return (
      !!heroImageUrl && (
        <div className="imagePreviewContainer">
          <Typography className="imagePreviewTitle" variant="captionLarge">
            {title}
          </Typography>
          <img
            className="imagePreview"
            src={heroImageUrl}
            alt={`heroImage ${title}`}
          />
        </div>
      )
    );
  }, [storeDetails, localImages]);

  const FillType = () => {
    return (
      <div>
        <FormControl>
          <RadioGroup
            row
            aria-labelledby="bannerToggleRadioGroup"
            value={currentFillType}
            name="bannerToggleRadioGroup"
            color="--color-slate"
            onChange={onHeaderTypeChange}>
            {/* <FormControlLabel
              value="solidColor"
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: '#615F5F'
                    }
                  }}
                />
              }
              label="Solid Red"
            /> */}
            <FormControlLabel
              value="none"
              className="fillTypeLabel"
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: 'var(--color-slate)'
                    }
                  }}
                />
              }
              label="None"
            />
            <FormControlLabel
              value="image"
              className="fillTypeLabel"
              disabled={!localImages.heroImage && !storeDetails.heroImageUrl}
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: 'var(--color-slate)'
                    }
                  }}
                />
              }
              label="Image"
            />
          </RadioGroup>
        </FormControl>
      </div>
    );
  };

  return (
    <div className="heroForm">
      <Typography variant="captionLarge">
        Customize the background of the banner area with a custom image or
        select none to keep it blank.
      </Typography>
      <div className="imageUploader">
        <Typography variant="captionLarge" className="imageUploaderHeader">
          Upload Image
        </Typography>
        <ImageUploader
          storeImageType="heroImage"
          onBlobUpload={onHeaderBlobChange}
          onCancel={onUploadCancel}
        />
        <Typography variant="captionSmall">
          Accepted file types: .png and .jpeg. Max file size: 5MB. Recommended
          dimensions: 1400 x 340px.
        </Typography>
        {imagePreview}
        <ClearExistingImage imageType="heroImage" />
      </div>
      <div className="bannerToggle">
        <span className="bannerToggleHeader">
          <Typography variant="captionLarge">Fill Type</Typography>
          <Tooltip
            className="infoTooltip"
            title="If you have uploaded a logo, that logo will appear in the banner area. If you have uploaded a banner image, that image will show in the banner area instead."
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: {className: 'tooltip'},
              arrow: {className: 'tooltipArrow'}
            }}>
            <img src={InfoIconSm} alt="Info" />
          </Tooltip>
        </span>
        <FillType />
      </div>
    </div>
  );
};

export default HeroImageForm;
